import { lazy, type ComponentType } from 'react'

// After a new version of MCCS is deployed, the previous build's JS chunks no
// longer exist on the server. Any browser tab that was already open (with an
// `index.html` referencing the old chunk hashes) will fail to lazy-load a
// route it hasn't visited yet, throwing an error like:
//   "TypeError: Failed to fetch dynamically imported module: ..."
//
// This wraps `React.lazy` so that failure is treated as "the app was
// upgraded" rather than a hard crash: we do a single automatic reload, which
// re-fetches the current `index.html` (pointing at the new build's chunks)
// and lets navigation continue transparently. If it fails again shortly
// after, something else is wrong, so we stop retrying and let the error
// propagate to the nearest error boundary.
const RELOAD_FLAG_KEY = 'mccs:chunk-reload-attempted-at'
const RELOAD_FLAG_TTL_MS = 10_000

const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  return CHUNK_LOAD_ERROR_PATTERN.test(message)
}

function hasReloadedRecently(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY)
    if (!raw) return false
    const timestamp = Number(raw)
    return (
      Number.isFinite(timestamp) && Date.now() - timestamp < RELOAD_FLAG_TTL_MS
    )
  } catch {
    // sessionStorage unavailable (e.g. private browsing edge cases) — assume
    // we haven't reloaded so we still get one recovery attempt.
    return false
  }
}

function markReloadAttempted(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()))
  } catch {
    // Ignore — worst case we lose the loop guard, not the recovery itself.
  }
}

/**
 * Drop-in replacement for `React.lazy` that automatically recovers from
 * stale-chunk errors caused by a deployment happening while the app was
 * open. See module docblock above for details.
 */
export function lazyImport<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory()
    } catch (error) {
      if (isChunkLoadError(error) && !hasReloadedRecently()) {
        markReloadAttempted()
        window.location.reload()
        // Suspend forever — the page is about to reload, so this component
        // never actually needs to resolve.
        return new Promise<{ default: T }>(() => {})
      }
      throw error
    }
  })
}
